import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Button,
  Spinner,
  Alert,
} from '@material-tailwind/react';
import { AgGridReact } from 'ag-grid-react';
import readXlsxFile from 'read-excel-file/browser';
import { collection, doc, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import db from '../../firebase';

// Aerista sheets only ever contain Cirrus aircraft.
const MANUFACTURER = 'cirrus';

const cleanReg = (reg) => (reg || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
// Excel date-only cells come through as UTC midnight, so format from UTC parts
// to avoid the day rolling back a day in timezones behind UTC.
const toYMD = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return '';
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${d.getUTCFullYear()}-${m}-${day}`;
};
// A date cell may arrive as a Date or an ISO string depending on the parser path.
const dateCell = (v) => {
  if (v instanceof Date) return toYMD(v);
  if (typeof v === 'string' && v) {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return toYMD(d);
  }
  return '';
};
const intOrNull = (v) => {
  if (v === '' || v == null) return null;
  const n = parseInt(String(v).replace(/[^0-9-]/g, ''), 10);
  return isNaN(n) ? null : n;
};

// Find a column index by any of its accepted header spellings.
const headerIndex = (headers, names) => {
  for (const n of names) {
    const i = headers.indexOf(n);
    if (i !== -1) return i;
  }
  return -1;
};

const validateRow = (r) => {
  const issues = [];
  if (!cleanReg(r.reg)) issues.push('reg');
  if (intOrNull(r.year) == null) issues.push('year');
  if (!String(r.model || '').trim()) issues.push('model');
  if (intOrNull(r.sale_price_usd) == null) issues.push('sold price');
  if (!r.dt_sold) issues.push('sold date');
  return issues;
};

const computeDocId = (r) => {
  const cr = cleanReg(r.reg);
  return cr && r.dt_sold ? `${cr}_${r.dt_sold}` : '';
};

function AeristaBulkUpload({ open, onClose, user, onUploaded }) {
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const gridApi = useRef(null);
  const fileInputRef = useRef(null);

  // Compute docId, validation issues, and DB-duplicate flag for each row (mutates in place).
  const annotate = useCallback(async (list) => {
    for (const r of list) {
      r._docId = computeDocId(r);
      r._issues = validateRow(r);
      r._dup = false;
      if (r._docId && !r._issues.length) {
        const snap = await getDoc(doc(db, 'lma_sales_comps', r._docId));
        // A soft-deleted doc is not a duplicate — uploading overwrites it with deleted: false.
        r._dup = snap.exists() && !snap.data().deleted;
      }
    }
  }, []);

  const handleFile = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setResult(null);
    setParsing(true);
    try {
      const parsedFile = await readXlsxFile(file);
      // v9 returns [{ sheet, data }]; older/option paths return a 2D array directly.
      const raw = Array.isArray(parsedFile?.[0]?.data) ? parsedFile[0].data : parsedFile;
      if (!Array.isArray(raw) || !raw.length) throw new Error('The sheet is empty.');
      const headers = raw[0].map((h) => String(h ?? '').trim().toLowerCase());
      const col = {
        year: headerIndex(headers, ['year']),
        model: headerIndex(headers, ['model']),
        gen: headerIndex(headers, ['gen', 'generation']),
        reg: headerIndex(headers, ['n#', 'reg', 'registration']),
        sn: headerIndex(headers, ['sn', 'serial', 'serial_no', 'serial no']),
        broker: headerIndex(headers, ['broker']),
        time: headerIndex(headers, ['time', 'total time', 'total_time']),
        list_price: headerIndex(headers, ['list price', 'listing price', 'list_price']),
        list_date: headerIndex(headers, ['list date', 'listing date', 'list_date']),
        sold_price: headerIndex(headers, ['sold price', 'sale price', 'sold_price']),
        sold_date: headerIndex(headers, ['sold date', 'sale date', 'sold_date']),
        dom: headerIndex(headers, ['days age', 'dom', 'days']),
      };
      const missing = ['reg', 'sold_date', 'sold_price'].filter((k) => col[k] === -1);
      if (missing.length) {
        throw new Error(
          `Couldn't find expected column(s): ${missing.join(', ')}. Is this an Aerista sheet?`
        );
      }
      const get = (r, i) => (i === -1 ? null : r[i]);
      const parsed = raw
        .slice(1)
        .filter((r) => r.some((c) => c != null && c !== ''))
        .map((r, i) => {
          const model = [get(r, col.model), get(r, col.gen)]
            .filter((v) => v != null && String(v).trim())
            .map((v) => String(v).trim())
            .join(' ');
          return {
            id: `row-${i}`,
            reg: get(r, col.reg) != null ? String(get(r, col.reg)).toUpperCase().trim() : '',
            year: get(r, col.year),
            model,
            serial_no: get(r, col.sn) != null ? String(get(r, col.sn)).trim() : '',
            broker: get(r, col.broker) != null ? String(get(r, col.broker)).trim() : '',
            total_time: get(r, col.time),
            orig_price_usd: get(r, col.list_price),
            sale_price_usd: get(r, col.sold_price),
            dt_listing: dateCell(get(r, col.list_date)),
            dt_sold: dateCell(get(r, col.sold_date)),
            dom: get(r, col.dom),
          };
        });
      if (!parsed.length) throw new Error('No data rows found below the header.');
      await annotate(parsed);
      setRows(parsed);
    } catch (err) {
      console.error('Aerista parse failed', err);
      setError(err.message || 'Failed to read the file.');
      setRows([]);
    } finally {
      setParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [annotate]);

  const recheck = useCallback(async () => {
    if (!gridApi.current) return;
    const current = [];
    gridApi.current.forEachNode((n) => current.push(n.data));
    setParsing(true);
    try {
      await annotate(current);
      gridApi.current.refreshCells({ force: true });
      setRows([...current]);
    } finally {
      setParsing(false);
    }
  }, [annotate]);

  const buildRecord = useCallback((r) => ({
    reg: r.reg.toUpperCase().trim(),
    year: intOrNull(r.year),
    model: String(r.model).trim(),
    pp_model: String(r.model).trim(),
    manufacturer: MANUFACTURER,
    serial_no: r.serial_no ? String(r.serial_no).trim() : null,
    broker: r.broker ? String(r.broker).trim() : null,
    total_time: intOrNull(r.total_time),
    orig_price_usd: intOrNull(r.orig_price_usd),
    sale_price_usd: intOrNull(r.sale_price_usd),
    dt_listing: r.dt_listing ? new Date(r.dt_listing) : null,
    dt_sold: new Date(r.dt_sold),
    dom: intOrNull(r.dom),
    notes: null,
    auction: false,
    deleted: false,
    enteredBy: user?.uid || null,
    enteredByName: user?.displayName || null,
    enteredAt: serverTimestamp(),
  }), [user]);

  const handleUpload = useCallback(async () => {
    if (!gridApi.current) return;
    setUploading(true);
    setError(null);
    try {
      const current = [];
      gridApi.current.forEachNode((n) => current.push(n.data));
      await annotate(current); // re-validate against any inline edits
      const toWrite = current.filter((r) => !r._dup && !r._issues.length);
      const dup = current.filter((r) => r._dup).length;
      const invalid = current.filter((r) => r._issues.length).length;
      if (!toWrite.length) {
        gridApi.current.refreshCells({ force: true });
        setError('No new valid rows to upload (all are duplicates or invalid).');
        return;
      }
      const batch = writeBatch(db);
      for (const r of toWrite) {
        const ref = doc(db, 'lma_sales_comps', r._docId);
        batch.set(ref, buildRecord(r));
        batch.set(doc(collection(ref, 'audit')), {
          action: 'created',
          source: 'Aerista bulk upload',
          timestamp: serverTimestamp(),
          userId: user?.uid || null,
          userName: user?.displayName || null,
        });
      }
      await batch.commit();
      setResult({ written: toWrite.length, dup, invalid });
      setRows([]);
      onUploaded?.();
    } catch (err) {
      console.error('Aerista upload failed', err);
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }, [annotate, buildRecord, onUploaded, user]);

  const colDefs = [
    { field: 'reg', headerName: 'Reg', editable: true, width: 95 },
    { field: 'year', headerName: 'Year', editable: true, width: 70 },
    { field: 'model', headerName: 'Model', editable: true, width: 110 },
    { field: 'serial_no', headerName: 'SN', editable: true, width: 90 },
    { field: 'broker', headerName: 'Broker', editable: true, width: 95 },
    { field: 'total_time', headerName: 'Time', editable: true, width: 75 },
    { field: 'orig_price_usd', headerName: 'List $', editable: true, width: 95 },
    { field: 'sale_price_usd', headerName: 'Sold $', editable: true, width: 95 },
    { field: 'dt_listing', headerName: 'Listed', editable: true, width: 105 },
    { field: 'dt_sold', headerName: 'Sold', editable: true, width: 105 },
    { field: 'dom', headerName: 'DOM', editable: true, width: 70 },
    {
      headerName: 'Status',
      width: 150,
      editable: false,
      valueGetter: (p) =>
        p.data._dup
          ? 'Duplicate (skip)'
          : p.data._issues?.length
          ? `Missing: ${p.data._issues.join(', ')}`
          : 'New',
      cellStyle: (p) =>
        p.data._dup
          ? { color: '#b45309' }
          : p.data._issues?.length
          ? { color: '#dc2626' }
          : { color: '#16a34a' },
    },
  ];

  const counts = rows.reduce(
    (acc, r) => {
      if (r._dup) acc.dup += 1;
      else if (r._issues?.length) acc.invalid += 1;
      else acc.valid += 1;
      return acc;
    },
    { valid: 0, dup: 0, invalid: 0 }
  );

  const handleClose = () => {
    setRows([]);
    setError(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      handler={handleClose}
      size="xl"
      className="!min-w-[70vw] !max-w-[1150px] !w-[90vw]"
    >
      <DialogHeader>Bulk Upload — Aerista Sales Comps</DialogHeader>
      <DialogBody className="space-y-3 max-h-[78vh] overflow-y-auto">
        {error && (
          <Alert color="red" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {result && (
          <Alert color="green">
            Uploaded {result.written} record{result.written === 1 ? '' : 's'}.
            {result.dup ? ` Skipped ${result.dup} duplicate(s).` : ''}
            {result.invalid ? ` Skipped ${result.invalid} invalid row(s).` : ''}
          </Alert>
        )}

        <div className="flex items-center gap-3 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFile}
            className="text-sm text-gray-800"
          />
          {parsing && <Spinner className="h-4 w-4" />}
          {rows.length > 0 && (
            <>
              <span className="text-sm font-semibold text-gray-800">
                <span className="text-green-700">{counts.valid} new</span>
                {counts.dup ? <span className="text-amber-700"> · {counts.dup} duplicate</span> : ''}
                {counts.invalid ? <span className="text-red-600"> · {counts.invalid} invalid</span> : ''}
              </span>
              <Button variant="text" size="sm" onClick={recheck} disabled={parsing}>
                Re-check
              </Button>
            </>
          )}
        </div>

        <p className="text-sm text-gray-700">
          Model and Gen are joined automatically (e.g. <code>SF50 G2</code>). Cells are editable —
          fix anything before uploading (e.g. change a <code>G2</code> to <code>G2+</code>). Rows
          already in the database are flagged as duplicates and skipped. Manufacturer is set to Cirrus.
        </p>

        {rows.length > 0 && (
          <div className="ag-theme-quartz" style={{ height: 380, width: '100%' }}>
            <AgGridReact
              rowData={rows}
              columnDefs={colDefs}
              defaultColDef={{ resizable: true, suppressMovable: true }}
              singleClickEdit
              stopEditingWhenCellsLoseFocus
              getRowId={(p) => p.data.id}
              onGridReady={(p) => { gridApi.current = p.api; }}
              domLayout="normal"
            />
          </div>
        )}
      </DialogBody>
      <DialogFooter className="gap-2">
        <Button variant="text" onClick={handleClose} disabled={uploading}>
          Close
        </Button>
        <Button
          onClick={handleUpload}
          disabled={uploading || parsing || counts.valid === 0}
          className="flex items-center gap-2"
        >
          {uploading && <Spinner className="h-4 w-4" />}
          Upload {counts.valid} record{counts.valid === 1 ? '' : 's'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

export default AeristaBulkUpload;
